import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Generic URL-backed dashboard filter / sort hook.
//
// Shared by the director dashboard (story 079) and the player
// dashboard (story 080). Each surface supplies a config object that
// names the chip slugs, the sort options, the classifier callbacks,
// and the comparators — the hook plumbs URL state, computes counts,
// and returns the derived list.
//
// Reads go through useSearchParams (so the hook re-renders on URL
// changes). Writes go through useNavigate and read window.location.search
// directly — React Router v7's setSearchParams' functional form
// captures the previous searchParams snapshot per-render, so rapid
// sequential writes from independent event handlers can stomp each
// other when React's batched re-render hasn't flushed yet. Reading
// window.location.search is always fresh.

export interface DashboardFilterConfig<I> {
  /** Visible chip slugs for status filtering, in render order. */
  statusChips: readonly string[];
  /** Visible chip slugs for time-bucket filtering, in render order. */
  timeChips: readonly string[];
  /**
   * Time-bucket chips whose items are filtered out when *no* time chip
   * is selected. The chip itself stays visible and clickable; selecting
   * it brings its items back in. Use sparingly — story 080's "past
   * hidden by default" is the motivating case.
   */
  timeChipsHiddenByDefault?: readonly string[];
  /** Sort option slugs offered in the dropdown, in render order. The
   *  list must include 'default' (no sort param in URL = this slug). */
  sortOptions: readonly string[];
  /** Map an item to its status-chip slug. Must return a value from statusChips. */
  classifyStatus: (item: I) => string;
  /** Map an item to all matching time-chip slugs (may be empty). */
  classifyTime: (item: I, now: Date) => string[];
  /** Predicate for search hits; `q` is already lowercased + trimmed. */
  matchesSearch: (item: I, q: string) => boolean;
  /** One comparator per sort option slug. Must cover every sortOptions entry. */
  comparators: Record<string, (a: I, b: I) => number>;
}

export interface DashboardFilters<I> {
  selectedStatuses: string[];
  selectedTimes: string[];
  search: string;
  sort: string;
  statusCounts: Record<string, number>;
  timeCounts: Record<string, number>;
  /** Items after status/time/search filtering and sorting. */
  filteredItems: I[];
  /** Any filter or sort active relative to the default state. */
  isFiltered: boolean;
  /** Config plumbed through for the toolbar to render chips + sort dropdown. */
  statusChips: readonly string[];
  timeChips: readonly string[];
  sortOptions: readonly string[];
  toggleStatus: (chip: string) => void;
  toggleTime: (chip: string) => void;
  setSearch: (q: string) => void;
  setSort: (s: string) => void;
  clearAll: () => void;
}

function parseCsv(raw: string | null, allowed: readonly string[]): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => allowed.includes(s));
}

export function useDashboardFilters<I>(
  items: I[],
  config: DashboardFilterConfig<I>,
): DashboardFilters<I> {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const selectedStatuses = useMemo(
    () => parseCsv(searchParams.get('status'), config.statusChips),
    [searchParams, config.statusChips],
  );
  const selectedTimes = useMemo(
    () => parseCsv(searchParams.get('time'), config.timeChips),
    [searchParams, config.timeChips],
  );
  const sort = useMemo<string>(() => {
    const raw = searchParams.get('sort') || 'default';
    return config.sortOptions.includes(raw) ? raw : 'default';
  }, [searchParams, config.sortOptions]);
  const search = searchParams.get('q') || '';

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(window.location.search);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      navigate({ search: next.toString() }, { replace: true });
    },
    [navigate],
  );

  const toggleStatus = useCallback(
    (chip: string) => {
      const current = parseCsv(
        new URLSearchParams(window.location.search).get('status'),
        config.statusChips,
      );
      const updated = current.includes(chip)
        ? current.filter((c) => c !== chip)
        : [...current, chip];
      updateParam('status', updated.length === 0 ? null : updated.join(','));
    },
    [updateParam, config.statusChips],
  );

  const toggleTime = useCallback(
    (chip: string) => {
      const current = parseCsv(
        new URLSearchParams(window.location.search).get('time'),
        config.timeChips,
      );
      const updated = current.includes(chip)
        ? current.filter((c) => c !== chip)
        : [...current, chip];
      updateParam('time', updated.length === 0 ? null : updated.join(','));
    },
    [updateParam, config.timeChips],
  );

  const setSort = useCallback(
    (s: string) => {
      updateParam('sort', s === 'default' ? null : s);
    },
    [updateParam],
  );

  const setSearch = useCallback(
    (q: string) => {
      updateParam('q', q === '' ? null : q);
    },
    [updateParam],
  );

  const clearAll = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete('status');
    next.delete('time');
    next.delete('q');
    next.delete('sort');
    navigate({ search: next.toString() }, { replace: true });
  }, [navigate]);

  // Counts are over the full unfiltered list — chip badges answer
  // "if I clicked only this, how many would I see?" and shouldn't
  // shift around when other filters change.
  const statusCounts = useMemo(() => {
    const out: Record<string, number> = Object.fromEntries(
      config.statusChips.map((c) => [c, 0]),
    );
    for (const item of items) {
      const chip = config.classifyStatus(item);
      if (chip in out) out[chip]++;
    }
    return out;
  }, [items, config]);

  const now = useMemo(() => new Date(), []);

  const timeCounts = useMemo(() => {
    const out: Record<string, number> = Object.fromEntries(
      config.timeChips.map((c) => [c, 0]),
    );
    for (const item of items) {
      for (const chip of config.classifyTime(item, now)) {
        if (chip in out) out[chip]++;
      }
    }
    return out;
  }, [items, config, now]);

  const filteredItems = useMemo(() => {
    const statusSet = new Set(selectedStatuses);
    const timeSet = new Set(selectedTimes);
    const q = search.trim().toLowerCase();
    const hiddenByDefault = new Set(config.timeChipsHiddenByDefault ?? []);

    const filtered = items.filter((item) => {
      if (statusSet.size > 0 && !statusSet.has(config.classifyStatus(item))) {
        return false;
      }
      const chips = config.classifyTime(item, now);
      if (timeSet.size > 0) {
        if (!chips.some((c) => timeSet.has(c))) return false;
      } else if (hiddenByDefault.size > 0) {
        // No time chip selected: items whose *only* time-bucket
        // classifications are all hidden-by-default get filtered out.
        // Items with no time-bucket classification at all pass through.
        if (chips.length > 0 && chips.every((c) => hiddenByDefault.has(c))) {
          return false;
        }
      }
      if (q && !config.matchesSearch(item, q)) return false;
      return true;
    });

    const cmp = config.comparators[sort] ?? config.comparators['default'];
    return filtered.slice().sort(cmp);
  }, [items, selectedStatuses, selectedTimes, search, sort, now, config]);

  const isFiltered =
    selectedStatuses.length > 0 ||
    selectedTimes.length > 0 ||
    search.length > 0 ||
    sort !== 'default';

  return {
    selectedStatuses,
    selectedTimes,
    search,
    sort,
    statusCounts,
    timeCounts,
    filteredItems,
    isFiltered,
    statusChips: config.statusChips,
    timeChips: config.timeChips,
    sortOptions: config.sortOptions,
    toggleStatus,
    toggleTime,
    setSearch,
    setSort,
    clearAll,
  };
}
